/**
 * IndexedDB utility for caching large files like audio recordings
 * This allows recovering from failed uploads on mobile devices
 */

const DB_NAME = 'recordingsCache';
const STORE_NAME = 'recordings';
const UPLOAD_PROGRESS_STORE = 'uploadProgress';
const DB_VERSION = 2; // Incremented for new store

/**
 * Initialize the IndexedDB database
 * @returns {Promise<IDBDatabase>} - The opened database
 */
const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        console.log('IndexedDB recordings store created');
      }
      // Add upload progress store for resumable uploads
      if (!db.objectStoreNames.contains(UPLOAD_PROGRESS_STORE)) {
        db.createObjectStore(UPLOAD_PROGRESS_STORE, { keyPath: 'recordingId' });
        console.log('IndexedDB upload progress store created');
      }
    };

    request.onsuccess = (event) => {
      const db = event.target.result;
      resolve(db);
    };

    request.onerror = (event) => {
      console.error('Error opening IndexedDB:', event.target.error);
      reject(event.target.error);
    };
  });
};

/**
 * Save a recording to IndexedDB
 * @param {Object} recording - The recording object to save
 * @param {string} recording.id - Unique ID for the recording (videoId_timestamp_narrator)
 * @param {number} recording.videoId - ID of the video being recorded
 * @param {string} recording.narrator - Narrator name
 * @param {Blob} recording.blob - The audio blob
 * @param {string} recording.filename - The filename
 * @param {Date} recording.timestamp - When the recording was created
 * @returns {Promise<string>} - The ID of the saved recording
 */
export const saveRecording = async (recording) => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.put(recording);
      
      request.onsuccess = () => {
        console.log('Recording saved to IndexedDB:', recording.id);
        resolve(recording.id);
      };
      
      request.onerror = (event) => {
        console.error('Error saving recording to IndexedDB:', event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error('Failed to save recording:', error);
    throw error;
  }
};

/**
 * Get all saved recordings for a specific video and narrator
 * @param {number} videoId - The video ID
 * @param {string} narrator - The narrator name
 * @returns {Promise<Array>} - Array of saved recordings
 */
export const getSavedRecordings = async (videoId, narrator) => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();
      
      request.onsuccess = () => {
        const allRecordings = request.result || [];
        // Filter by videoId and narrator
        const filteredRecordings = allRecordings.filter(
          rec => rec.videoId === videoId && rec.narrator === narrator
        );
        
        // Sort by timestamp (newest first)
        filteredRecordings.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        
        resolve(filteredRecordings);
      };
      
      request.onerror = (event) => {
        console.error('Error retrieving recordings:', event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error('Failed to get saved recordings:', error);
    return [];
  }
};

/**
 * Delete a recording from IndexedDB
 * @param {string} id - The recording ID to delete
 * @returns {Promise<void>}
 */
export const deleteRecording = async (id) => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);
      
      request.onsuccess = () => {
        console.log('Recording deleted from IndexedDB:', id);
        resolve();
      };
      
      request.onerror = (event) => {
        console.error('Error deleting recording:', event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error('Failed to delete recording:', error);
    throw error;
  }
};

/**
 * Save upload progress for resumable uploads
 * @param {Object} progress - Upload progress data
 * @param {string} progress.recordingId - The recording ID this upload is for
 * @param {string} progress.uploadId - The server-side upload session ID
 * @param {number} progress.videoId - Video ID
 * @param {string} progress.narrator - Narrator name
 * @param {number[]} progress.completedChunks - Array of completed chunk indices
 * @param {number} progress.totalChunks - Total number of chunks
 * @param {string} progress.timestamp - ISO timestamp of last update
 * @returns {Promise<string>} - The recording ID
 */
export const saveUploadProgress = async (progress) => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([UPLOAD_PROGRESS_STORE], 'readwrite');
      const store = transaction.objectStore(UPLOAD_PROGRESS_STORE);
      
      const request = store.put(progress);
      
      request.onsuccess = () => {
        console.log('Upload progress saved:', progress.recordingId, `${progress.completedChunks.length}/${progress.totalChunks} chunks`);
        resolve(progress.recordingId);
      };
      
      request.onerror = (event) => {
        console.error('Error saving upload progress:', event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error('Failed to save upload progress:', error);
    throw error;
  }
};

/**
 * Get upload progress for a recording
 * @param {string} recordingId - The recording ID
 * @returns {Promise<Object|null>} - Upload progress object or null if not found
 */
export const getUploadProgress = async (recordingId) => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([UPLOAD_PROGRESS_STORE], 'readonly');
      const store = transaction.objectStore(UPLOAD_PROGRESS_STORE);
      const request = store.get(recordingId);
      
      request.onsuccess = () => {
        resolve(request.result || null);
      };
      
      request.onerror = (event) => {
        console.error('Error retrieving upload progress:', event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error('Failed to get upload progress:', error);
    return null;
  }
};

/**
 * Delete upload progress for a recording
 * @param {string} recordingId - The recording ID
 * @returns {Promise<void>}
 */
export const deleteUploadProgress = async (recordingId) => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([UPLOAD_PROGRESS_STORE], 'readwrite');
      const store = transaction.objectStore(UPLOAD_PROGRESS_STORE);
      const request = store.delete(recordingId);
      
      request.onsuccess = () => {
        console.log('Upload progress deleted:', recordingId);
        resolve();
      };
      
      request.onerror = (event) => {
        console.error('Error deleting upload progress:', event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error('Failed to delete upload progress:', error);
    throw error;
  }
};
